/**
 * deliberationStore — the write and read paths for the Deliberation Record
 * (Doc 21 §6.4; tables from migration 495).
 *
 * One record per session (UNIQUE session_id). Three signal writers land here:
 *   addAnnotation              — per-item rationale note (optional, any time)
 *   editAnnotation             — the author reshapes their own note (RLS dia_update)
 *   retractAnnotation          — the author takes it back; the row is kept (mig 746)
 *   postMessage                — the session thread (optional)
 *   recordOverrideJustifications — the facilitator override reason (MANDATORY)
 * Nothing here blocks a session from closing. The session report snapshot is
 * built server-side by the 726 trigger, so this store never writes reports.
 */
import { create } from 'zustand';
import { getSafeSupabaseClient } from '../../lib/supabaseSync';
import { generateUUID } from '../../lib/supabaseSync';
import {
  deliberationMessageFromRow,
  itemAnnotationFromRow,
  overrideJustificationFromRow,
  type DeliberationMessage,
  type DeliberationMessageRow,
  type ItemAnnotation,
  type ItemAnnotationRow,
  type OverrideJustification,
  type OverrideJustificationRow,
} from '../../types/prioritization/deliberation';
import { normalizeRationale } from '../../lib/deliberationCore';
import { retractDeliberationNote, type ReversalResult } from '../../lib/reversals/reversalsService';

interface RecordRef {
  id: string;
  sessionId: string;
  workspaceId: string;
}

interface DeliberationState {
  records: Record<string, RecordRef>;
  annotations: ItemAnnotation[];
  messages: DeliberationMessage[];
  overrides: OverrideJustification[];
  loadedSessions: Record<string, true>;
  _channelSessionId: string | null;
  _channel: unknown;

  ensureRecord: (sessionId: string, workspaceId: string) => Promise<RecordRef | null>;
  fetchForSession: (sessionId: string) => Promise<void>;
  addAnnotation: (input: { sessionId: string; workspaceId: string; itemId: string; authorId: string; content: string }) => Promise<{ ok: boolean; error?: string }>;
  editAnnotation: (annotationId: string, content: string) => Promise<{ ok: boolean; error?: string }>;
  /**
   * Retract your own note with a reason (retract_deliberation_note, mig 746).
   * Returns the server's ReversalResult so a refusal reaches the UI verbatim.
   */
  retractAnnotation: (annotationId: string, reason: string) => Promise<ReversalResult>;
  postMessage: (input: { sessionId: string; workspaceId: string; authorId: string; authorName: string; content: string }) => Promise<{ ok: boolean; error?: string }>;
  recordOverrideJustifications: (input: {
    sessionId: string;
    workspaceId: string;
    actorId: string;
    overrides: Array<{ itemId: string; aggregateResult: unknown; overrideResult: unknown; reason: string }>;
  }) => Promise<{ ok: boolean; error?: string; written: number }>;
  subscribe: (sessionId: string) => void;
  unsubscribe: () => void;
}

export const useDeliberationStore = create<DeliberationState>()((set, get) => ({
  records: {},
  annotations: [],
  messages: [],
  overrides: [],
  loadedSessions: {},
  _channelSessionId: null,
  _channel: null,

  ensureRecord: async (sessionId, workspaceId) => {
    const cached = get().records[sessionId];
    if (cached) return cached;
    const supabase = getSafeSupabaseClient();
    if (!supabase) return null;
    const { data } = await supabase.from('deliberation_records').select('id, session_id, workspace_id').eq('session_id', sessionId).maybeSingle();
    if (data) {
      const ref = { id: data.id as string, sessionId, workspaceId: (data.workspace_id as string) ?? workspaceId };
      set((s) => ({ records: { ...s.records, [sessionId]: ref } }));
      return ref;
    }
    // Created with the session normally (sessionStore); recreate if missing — never deleted, so this is rare.
    const id = generateUUID();
    const { error } = await supabase.from('deliberation_records').insert({ id, session_id: sessionId, workspace_id: workspaceId, transcript_status: 'none' });
    if (error) {
      console.warn('[DeliberationStore] could not create record:', error.message);
      return null;
    }
    const ref = { id, sessionId, workspaceId };
    set((s) => ({ records: { ...s.records, [sessionId]: ref } }));
    return ref;
  },

  fetchForSession: async (sessionId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    const [ann, msg, ovr] = await Promise.all([
      // Notes only: a transcript passage the AI proposed is not a note until a person confirms it (mig 831).
      supabase.from('deliberation_item_annotations').select('*').eq('session_id', sessionId).eq('status', 'confirmed').order('created_at', { ascending: true }),
      supabase.from('deliberation_messages').select('*').eq('session_id', sessionId).order('created_at', { ascending: true }),
      supabase.from('deliberation_override_justifications').select('*').eq('session_id', sessionId).order('created_at', { ascending: true }),
    ]);
    set((s) => ({
      annotations: [...s.annotations.filter((a) => a.sessionId !== sessionId), ...((ann.data ?? []) as ItemAnnotationRow[]).map(itemAnnotationFromRow)],
      messages: [...s.messages.filter((m) => m.sessionId !== sessionId), ...((msg.data ?? []) as DeliberationMessageRow[]).map(deliberationMessageFromRow)],
      overrides: [...s.overrides.filter((o) => o.sessionId !== sessionId), ...((ovr.data ?? []) as OverrideJustificationRow[]).map(overrideJustificationFromRow)],
      loadedSessions: { ...s.loadedSessions, [sessionId]: true },
    }));
  },

  addAnnotation: async ({ sessionId, workspaceId, itemId, authorId, content }) => {
    const text = normalizeRationale(content);
    if (!text) return { ok: false, error: 'Write the reason first.' };
    const record = await get().ensureRecord(sessionId, workspaceId);
    if (!record) return { ok: false, error: 'Deliberation record unavailable.' };
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { ok: false, error: 'Database not available.' };
    const row = {
      id: generateUUID(),
      deliberation_record_id: record.id,
      session_id: sessionId,
      workspace_id: record.workspaceId,
      item_id: itemId,
      author_id: authorId,
      content: text,
      source: 'manual',
    };
    const { data, error } = await supabase.from('deliberation_item_annotations').insert(row).select('*').single();
    if (error) return { ok: false, error: error.message };
    const annotation = itemAnnotationFromRow(data as ItemAnnotationRow);
    set((s) => (s.annotations.some((a) => a.id === annotation.id) ? s : { annotations: [...s.annotations, annotation] }));
    return { ok: true };
  },

  editAnnotation: async (annotationId, content) => {
    const text = normalizeRationale(content);
    if (!text) return { ok: false, error: 'A note cannot be empty — retract it instead.' };
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { ok: false, error: 'Database not available.' };
    // RLS (dia_update) limits this to the author; the 746 trigger refuses edits
    // to a retracted note. No local change until the server confirms the row.
    const { data, error } = await supabase
      .from('deliberation_item_annotations')
      .update({ content: text })
      .eq('id', annotationId)
      .select('*')
      .maybeSingle();
    if (error) return { ok: false, error: error.message };
    if (!data) return { ok: false, error: 'Only the author can edit this note.' };
    const updated = itemAnnotationFromRow(data as ItemAnnotationRow);
    set((s) => ({ annotations: s.annotations.map((a) => (a.id === updated.id ? updated : a)) }));
    return { ok: true };
  },

  retractAnnotation: async (annotationId, reason) => {
    const result = await retractDeliberationNote(annotationId, reason);
    if (result.ok) {
      const at = new Date().toISOString();
      set((s) => ({ annotations: s.annotations.map((a) => (a.id === annotationId ? { ...a, retractedAt: at } : a)) }));
    }
    return result;
  },

  postMessage: async ({ sessionId, workspaceId, authorId, authorName, content }) => {
    const text = normalizeRationale(content);
    if (!text) return { ok: false, error: 'Nothing to post.' };
    const record = await get().ensureRecord(sessionId, workspaceId);
    if (!record) return { ok: false, error: 'Deliberation record unavailable.' };
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { ok: false, error: 'Database not available.' };
    const row = {
      id: generateUUID(),
      deliberation_record_id: record.id,
      session_id: sessionId,
      workspace_id: record.workspaceId,
      author_id: authorId,
      author_name: authorName,
      content: text,
    };
    const { data, error } = await supabase.from('deliberation_messages').insert(row).select('*').single();
    if (error) return { ok: false, error: error.message };
    const message = deliberationMessageFromRow(data as DeliberationMessageRow);
    set((s) => (s.messages.some((m) => m.id === message.id) ? s : { messages: [...s.messages, message] }));
    return { ok: true };
  },

  recordOverrideJustifications: async ({ sessionId, workspaceId, actorId, overrides }) => {
    if (overrides.length === 0) return { ok: true, written: 0 };
    const missing = overrides.filter((o) => !normalizeRationale(o.reason));
    if (missing.length > 0) {
      return { ok: false, written: 0, error: `Every override needs a reason (${missing.length} missing).` };
    }
    const record = await get().ensureRecord(sessionId, workspaceId);
    if (!record) return { ok: false, written: 0, error: 'Deliberation record unavailable.' };
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { ok: false, written: 0, error: 'Database not available.' };
    const rows = overrides.map((o) => ({
      id: generateUUID(),
      deliberation_record_id: record.id,
      session_id: sessionId,
      workspace_id: record.workspaceId,
      item_id: o.itemId,
      overridden_by: actorId,
      aggregate_result: o.aggregateResult ?? {},
      override_result: o.overrideResult ?? {},
      reason: normalizeRationale(o.reason) as string,
    }));
    const { data, error } = await supabase.from('deliberation_override_justifications').insert(rows).select('*');
    if (error) return { ok: false, written: 0, error: error.message };
    const written = ((data ?? []) as OverrideJustificationRow[]).map(overrideJustificationFromRow);
    set((s) => ({ overrides: [...s.overrides, ...written.filter((w) => !s.overrides.some((o) => o.id === w.id))] }));
    return { ok: true, written: written.length };
  },

  subscribe: (sessionId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    if (get()._channelSessionId === sessionId) return;
    get().unsubscribe();
    const channel = supabase
      .channel(`session-deliberation:${sessionId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'deliberation_messages', filter: `session_id=eq.${sessionId}` }, (payload) => {
        const message = deliberationMessageFromRow(payload.new as DeliberationMessageRow);
        set((s) => (s.messages.some((m) => m.id === message.id) ? s : { messages: [...s.messages, message] }));
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'deliberation_item_annotations', filter: `session_id=eq.${sessionId}` }, (payload) => {
        const row = payload.new as ItemAnnotationRow;
        if (row.status && row.status !== 'confirmed') return; // a proposal, not a note (mig 831)
        const annotation = itemAnnotationFromRow(row);
        set((s) => (s.annotations.some((a) => a.id === annotation.id) ? s : { annotations: [...s.annotations, annotation] }));
      })
      .subscribe();
    set({ _channel: channel, _channelSessionId: sessionId });
  },

  unsubscribe: () => {
    const supabase = getSafeSupabaseClient();
    const ch = get()._channel;
    if (supabase && ch) void supabase.removeChannel(ch as Parameters<typeof supabase.removeChannel>[0]);
    set({ _channel: null, _channelSessionId: null });
  },
}));
